import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * GradientOrbs — three sage radial-gradient orbs that drift slowly across the page.
 *
 * Top-left orb (640×640), bottom-right orb (580×580), center breath orb (420×420).
 * Heartbeat ring fires every 8s on the top-left orb.
 *
 * Mobile: skipped entirely — blur(48px) × 3 orbs is the most expensive thing
 * we could do on iOS. StarField alone provides sufficient ambience.
 */

const IS_MOBILE = typeof window !== 'undefined' && window.innerWidth < 768;

interface GradientOrbsProps {
  heartbeat?: boolean;
  rgb?: [number, number, number];
  masterOpacity?: number;
}

export function GradientOrbs({ heartbeat = true, rgb = [113, 180, 112], masterOpacity = 1 }: GradientOrbsProps) {
  const [pulse, setPulse] = useState(false);
  const [r, g, b] = rgb;

  useEffect(() => {
    if (!heartbeat || IS_MOBILE) return;
    const trigger = () => {
      setPulse(true);
      setTimeout(() => setPulse(false), 1200);
    };
    const initial  = setTimeout(trigger, 1800);
    const interval = setInterval(trigger, 8000);
    return () => { clearTimeout(initial); clearInterval(interval); };
  }, [heartbeat]);

  if (IS_MOBILE) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ overflow: 'visible', zIndex: 0, opacity: masterOpacity }}
    >
      {/* Top-left orb */}
      <div
        className="absolute"
        style={{ width: 640, height: 640, top: '-15%', left: '-12%', pointerEvents: 'none' }}
      >
        <motion.div
          className="absolute rounded-full"
          style={{
            width: 640, height: 640, top: 0, left: 0,
            background: `radial-gradient(circle, rgba(${r},${g},${b},0.13) 0%, transparent 65%)`,
            filter: 'blur(48px)',
            pointerEvents: 'none',
          }}
          animate={{ x: [0, 40, -20, 0], y: [0, 30, -15, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />
        {pulse && (
          <motion.div
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 640, height: 640, top: 0, left: 0,
              border: `1px solid rgba(${r},${g},${b},0.12)`,
            }}
            initial={{ scale: 0.85, opacity: 0.3 }}
            animate={{ scale: 1.1,  opacity: 0 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          />
        )}
      </div>

      {/* Bottom-right orb */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 580, height: 580,
          bottom: '-15%', right: '-10%',
          background: `radial-gradient(circle, rgba(${r},${g},${b},0.13) 0%, transparent 62%)`,
          filter: 'blur(48px)',
          pointerEvents: 'none',
        }}
        animate={{ x: [0, -35, 20, 0], y: [0, -25, 18, 0] }}
        transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
      />

      {/* Center breathing orb */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 420, height: 420,
          top: '35%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: `radial-gradient(circle, rgba(${r},${g},${b},0.08) 0%, transparent 58%)`,
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
        animate={{ scale: [1, 1.14, 1], opacity: [0.55, 1, 0.55] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      />
    </div>
  );
}
